'use client'

import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { Text } from '@wartek-id/text'

import { flattenNestedRows } from '@/utils/table'
import {
  trackViewDataset,
  trackViewDatasetEmptyState,
} from '@/utils/tracker/modules/dataset'
import { MAP_STATUS_BY_VALUE } from '@/apis/data/statistik/status'
import {
  BaseKeyVal,
  BasicDataRow,
  District,
  EducationStatus,
  MetaRow,
  NestedColumnType,
  NestedDataRow,
  StatisticDetailMeta,
  TableType,
} from '@/apis/types'

import { Container } from '@/components/Container'
import { EmptyState } from '@/components/EmptyState'
import { RoundedTable } from '@/components/RoundedTable/RoundedTable'

import { CustomStatisticCellRenderer } from './CustomStatisticCellRenderer'
import { CustomStatisticNestedCellRenderer } from './CustomStatisticNestedCellRenderer'
import { NestedTable } from './NestedTable/NestedTable'
import { BreadcrumbDistrict } from '../../BreadcrumbDistrict/BreadcrumbDistrict'

interface TableWrapperProps {
  tableType: TableType
  tableNum: string
  educationLevelId: number
  educationLevelName: string
  educationStatus?: EducationStatus
  yearPeriod: number
  columns: NestedColumnType[]
  data: BasicDataRow[] | NestedDataRow[]
  meta: StatisticDetailMeta
  districts: District[]
  districtId: string
  districtLevel: number
  titleWithTag: string
}

export const TableStatistik = ({
  tableType,
  tableNum,
  educationLevelId,
  educationLevelName,
  educationStatus,
  yearPeriod,
  columns = [],
  data,
  meta,
  districts,
  districtId,
  districtLevel,
  titleWithTag,
}: TableWrapperProps) => {
  const mountedRef = useRef<boolean>(false)
  const withStatusQuery =
    educationStatus && educationStatus !== 'EDUCATION_STATUS_UNSPECIFIED'
      ? `?status=${MAP_STATUS_BY_VALUE[educationStatus]?.toLowerCase()}`
      : ''

  function getRedirectLink(metaRow?: BaseKeyVal) {
    if (typeof metaRow === 'object' && metaRow['districtId']) {
      return `/dataset/detail/${tableNum}/L${metaRow['districtLevel']}-${metaRow['districtId']}/${yearPeriod}/${educationLevelName}-${educationLevelId}${withStatusQuery}#filter-section`
    }

    return ''
  }

  const hrefBuilderFn = (district: District) => {
    const fullLink = `/dataset/detail/${tableNum}/L${district.districtLevel}-${district.id}/${yearPeriod}/${educationLevelName}-${educationLevelId}${withStatusQuery}#filter-section`

    return fullLink
  }

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (data) {
      if ((data || []).length === 0) {
        timer = setTimeout(() => {
          if (!mountedRef.current) {
            trackViewDatasetEmptyState({
              tableNum,
              districtId,
              districtLevel,
              yearPeriod,
              educationLevelName,
              educationStatus,
            })
            mountedRef.current = true
          }
        }, 1000)
      } else {
        timer = setTimeout(() => {
          if (!mountedRef.current) {
            trackViewDataset({
              tableNum,
              districtId,
              districtLevel,
              yearPeriod,
              educationLevelName,
              educationStatus,
            })
            mountedRef.current = true
          }
        }, 1000)
      }
    }

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className="bg-gray-5 py-8" id="content-section">
      <Container>
        <Text
          variant="heading-md"
          className={clsx('pb-4 text-center md:text-left')}
        >
          {titleWithTag}
        </Text>

        <BreadcrumbDistrict
          districts={districts}
          districtId={districtId}
          districtLevel={districtLevel}
          hrefBuilderFn={hrefBuilderFn}
        />

        {data.length > 0 ? (
          <>
            {tableType === 'TABLE_TYPE_OVERALL' ? (
              <NestedTable
                columns={[
                  {
                    id: 'number',
                    displayName: 'No',
                  },
                  ...columns,
                ]}
                // @ts-ignore
                data={flattenNestedRows('variable', data, 1)}
                withSummary={false}
                CustomCellRenderer={({ row, col }) => {
                  return (
                    <CustomStatisticNestedCellRenderer row={row} col={col} />
                  )
                }}
              />
            ) : (
              <RoundedTable
                columns={columns}
                data={data?.map((row) => {
                  return {
                    ...row,
                    // @ts-ignore
                    ...(row?.meta || {}),
                  }
                })}
                withSummary={true}
                CustomCellRenderer={({ row, col }) => {
                  const originalRowData = row?.row?.original as MetaRow
                  const redirectLink = getRedirectLink(originalRowData?.meta)

                  return (
                    <CustomStatisticCellRenderer
                      row={row}
                      col={col}
                      redirectLink={redirectLink}
                    />
                  )
                }}
              />
            )}
          </>
        ) : (
          <EmptyState
            className="mb-4 rounded-lg"
            title="Oops, data kosong"
            description={`Dataset statistik ini belum tersedia, silahkan coba pilihan lain.`}
          />
        )}

        {meta?.cutoffDate ? (
          <div className="w-full">
            <Text className="italic">Tanggal cut off: {meta?.cutoffDate}</Text>
          </div>
        ) : null}
      </Container>
    </section>
  )
}
